import React, { useCallback, useEffect, useRef, useState } from 'react';
import styled from '@emotion/styled';
import config from 'config';
import { OCEAN_THEME, colors } from 'global/variables';
import { useDispatch, useSelector } from 'react-redux';
import { get, isEmpty, set, isEqual, last } from 'lodash';
import { JsonViewer, applyValue } from '@textea/json-viewer';
import {
  generateAvoidAreasFromFileProblem,
  generateExcludeAreasFromFileProblem,
  generatePolygonsFromOrders,
} from 'utils/territories/TerritoriesFromJson';
import {
  changeJsonOrderLocation,
  clearAll,
  convertOrderToJson,
  fetchAndProcessFiles,
  handleJsonDrop,
  integrateFileText,
  recalculateMapData,
  recalculateSolutionParams,
} from 'utils/apiFilesUploadHelpers';
import {
  clearSolution,
  setDisplayParam,
  setError,
  setOrders,
  setProblemJsonParam,
  setSolutionJson,
  setSolutionParam,
  setTerritoriesFromProblemFile,
  setTourParameter,
  setUserParam,
} from 'actions';
import { useCookies } from 'react-cookie';
import FileUploadArea from 'components/Wizard/Step2/FileUploadArea';
import { ToastContainer, toast } from 'react-toastify';
import entryPointIcon from '../../../global/img/dev-console.svg';
import Modal from '../../Presentation/Modal';
import {
  checkForValidTPJson,
  convertToValidJson,
  formatErrorPath,
  getAsset,
  removeAllWhitespaces,
} from '../../../utils/helpers';
import { AMPLITUDE_EVENTS, AmplitudeService } from '../../../utils/amplitude';
import withTranslation from '../../../hoc/withTranslation';
import { StyledContrastPanel } from '../../Wizard/Global/WizardStyledContrastPanel';
import { getAppMode } from '../../../utils/urlHelpers';
import DevConsoleHeader, { METHODS } from './DevConsoleHeader';
import DevConsoleFooter from './DevConsoleFooter';
import {
  getLastValidIteration,
  getToursDataFromIteration,
  hasOngoingAsyncRequest,
  isIterationSolution,
  removeProperties,
} from '../../../utils/SolutionHelpers';
import pasteIcon from '../../../global/img/paste.svg';
import copyIcon from '../../../global/img/copy_white.svg';

const { white, secondaryColor, primaryColor } = colors;
const appMode = getAppMode();

const {
  requestTypes: { async },
} = config;

const StyledFloatingBtnContainer = styled.div({
  display: 'flex',
  flexDirection: 'column',
});

const StyledFloatingBtn = styled.button(({ icon, order, disabled }) => ({
  position: 'fixed',
  top: order === 2 ? '212px' : '170px',
  border: 'none',
  transition: 'background-color 0.3s ease-in-out',
  cursor: 'pointer',
  zIndex: 100,
  height: '40px',
  width: '36px',
  right: '18px',
  backgroundColor: 'transparent',
  boxShadow: '0 6px 6px rgba(0, 0, 0, 0.6)',
  backgroundImage: `url(${getAsset(icon)})`,
  backgroundRepeat: 'no-repeat',
  backgroundPosition: 'center',
  pointerEvents: disabled && 'none',
  opacity: disabled && 0.4,
  '&:hover': {
    backgroundColor: 'rgba(256, 256, 256, 0.1)',
  },
}));

const StyledAccessContainer = styled.div({
  position: 'fixed',
  top: '12rem',
  right: '1rem',
  width: '2.5rem',
  height: '2.5rem',
  borderRadius: '2rem',
  backgroundColor: white,
  cursor: 'pointer',
  marginTop: '1rem',
  backgroundImage: `linear-gradient(
    to right,
    ${secondaryColor} 0%,
    ${primaryColor} 51%,
    ${secondaryColor} 100%
  )`,
  backgroundRepeat: 'no-repeat',
  backgroundPosition: 'center center',
  backgroundSize: '100%',
  zIndex: 10099,
  boxShadow: '0px 10px 29px 0px rgba(0, 0, 0, 0.22)',
  transition: 'all 250ms cubic-bezier(0.365, 0.005, 0.285, 1.005)',
  animationDuration: '.8s',
  animationTimingFunction: 'ease-in-out',
  animationIterationCount: 'infinite',
  div: {
    backgroundImage: `url(${getAsset(entryPointIcon)})`,
    backgroundPosition: 'center center',
    backgroundSize: '1.3rem',
    backgroundRepeat: 'no-repeat',
    width: '100%',
    height: '100%',
    ':hover': {
      backgroundSize: '1.4rem',
    },
  },
  '@media screen and (max-width: 850px)': {
    display: 'none',
  },
});

const StyledJsonContainer = styled.div({
  height: 'calc(100% - 14.5rem)',
  overflow: 'scroll',
  padding: '1rem 1rem 0 1rem',
  boxShadow: '0px 10px 29px 0px rgba(0, 0, 0, 0.3)',
  borderRight: 'none',
  backgroundColor: `rgba(255,255,255,0)`,
});

const OPTIONS = {
  PROBLEM: 'problem',
  SOLUTION: 'solution',
  INTERNAL_TOUR_PLANNER: 'tourPlanner',
  INTERNAL_USER: 'user',
  INTERNAL_ITERATIONS: 'iterations',
};

const DevConsole = ({
  translations: {
    error,
    devConsoleLabels,
    jsonViewer: {
      pasteErrors,
      successfulPaste,
      successfulCopy,
      actions: { pasteJson, copyJson },
    },
    map: { returnLocationMarkerTrans, depotMarkerTrans },
  },
  oAuth,
  solution,
  errorState,
}) => {
  const dispatch = useDispatch();
  const [cookies] = useCookies(['apikey']);
  const user = useSelector(({ user: stateUser }) => stateUser);
  const orders = useSelector((state) => state.orders[solution.show]);
  const filtered = getLastValidIteration(solution.requests[solution.show]);
  const tourData = getToursDataFromIteration(filtered);
  const territories = useSelector(({ areas: stateAreas }) => stateAreas[solution.show].territories);
  const tourPlanner = useSelector(
    ({ tourPlanner: stateTourPlanner }) => stateTourPlanner.value[solution.show],
  );
  const loader = useSelector(({ loader: stateLoader }) => stateLoader);
  const pasteDisabled = loader.isLoading || hasOngoingAsyncRequest(solution.requests);
  const tempOrders = solution.requests[solution.show].tempOrders;

  const handleSetProblemJsonParam = useCallback(
    (param) => dispatch(setProblemJsonParam(param)),
    [dispatch],
  );

  const handleSetError = useCallback((data) => dispatch(setError(data)), [dispatch]);

  const handleSetSolutionJson = useCallback(
    (param) => dispatch(setSolutionJson(param)),
    [dispatch],
  );
  const handleSetUserParam = useCallback((data) => dispatch(setUserParam(data)), [dispatch]);
  const handleSetSolutionParameter = useCallback(
    (data) => dispatch(setSolutionParam(data)),
    [dispatch],
  );
  const handleSetTourParameter = useCallback(
    (parameter) => dispatch(setTourParameter(parameter)),
    [dispatch],
  );
  const handleSetOrders = useCallback(
    (newOrders) => dispatch(setOrders(newOrders, false, solution.show)),
    [dispatch, solution.show],
  );
  const jsonRef = useRef();
  const oldValueRef = useRef([]);
  const updateRef = useRef(false);
  const clearedTerritoriesRef = useRef(false);
  const positionsClearedRef = useRef(false);
  const priorityClearedRef = useRef(false);
  const groupsClearedRef = useRef(false);
  const jsonObjectRef = useRef();
  const maxTimeClearedRef = useRef(false);

  const [jsonObject, setJsonObject] = useState();
  const [selectedOption, setSelectedOption] = useState(OPTIONS.PROBLEM);
  const [isJsonUploadClicked, setIsJsonUploadClicked] = useState(false);
  const [showJsonPanel, setShowJsonPanel] = useState(false);
  const [isTourPanelOpen, setIsTourPanelOpen] = useState(false);
  const [resetValue, setResetValue] = useState(false);
  const [justPastedJson, setJustPastedJson] = useState(false);
  const fileName = solution.requests[solution.show].name;
  const fileExt = fileName ? fileName.split('.').pop() : 'json';
  const convertToTerritory = user.mapSettings.convertToTerritory;
  const isSolution =
    solution && isIterationSolution(last(solution.requests[solution.show].iterations));
  const handleSetDisplayParameter = useCallback(
    (data) => dispatch(setDisplayParam(data)),
    [dispatch],
  );
  const reg = /(['])(?:(?=(\\?))\2.)*?\1/g;
  const pathReg = errorState?.errorMessage?.match(reg);
  const isEditable = selectedOption === OPTIONS.PROBLEM;

  useEffect(() => {
    fetchAndProcessFiles(dispatch, oAuth, user, cookies, error, solution, setShowJsonPanel);
  }, [dispatch, oAuth, setShowJsonPanel, error]);

  const escFunction = useCallback(
    (event) => {
      if (event.keyCode === 27) {
        // eslint-disable-next-line no-use-before-define
        togglePanel('close');
      }
    },
    [showJsonPanel, user.jsonTourPanelOpen, user.jsonPanelOpen],
  );

  function assignTerritoriesToOrders(tours = [], ords = []) {
    const jobToVehicleMap = {};

    tours?.forEach((tour) => {
      const vehicleId = tour.vehicleId || 'default';
      tour.stops?.forEach((stop) => {
        stop.activities?.forEach((activity) => {
          const { jobId, type } = activity;
          if (jobId && type !== 'departure' && type !== 'arrival') {
            jobToVehicleMap[jobId] = vehicleId;
          }
        });
      });
    });

    const uniqueOrders = [];
    const seenIds = new Set();
    ords.forEach((order) => {
      if (!seenIds.has(order.InternalID)) {
        uniqueOrders.push(order);
        seenIds.add(order.InternalID);
      }
    });

    return uniqueOrders.map((order) => {
      const territoryName = jobToVehicleMap[order.InternalID];
      return {
        ...order,
        ...(territoryName &&
          territoryName !== 'default' && {
            Territories: [territoryName],
          }),
      };
    });
  }

  const togglePanel = useCallback(
    (forceAction) => {
      const action = forceAction === undefined ? (showJsonPanel ? 'close' : 'open') : forceAction;
      const info = { action };
      const newState = action === 'open';
      AmplitudeService.log(AMPLITUDE_EVENTS.DEVELOPER_CONSOLE_ACTION, info);
      setShowJsonPanel(newState);
      handleSetUserParam({ jsonPanelOpen: newState });

      if (action === 'close') {
        setShowJsonPanel(false);
        handleSetUserParam({ jsonTourPanelOpen: false, jsonPanelOpen: false });
      }
    },
    [setShowJsonPanel, showJsonPanel, handleSetUserParam],
  );

  const handleDrop = useCallback(
    (files, mode, element) => {
      dispatch(setSolutionJson({ jsonNoTerritories: false }));

      if (files[0].name.endsWith('.csv')) {
        handleSetError(error.csvUploadError);
        element.current.value = '';
      } else {
        handleSetTourParameter({ ordersMode: METHODS.JSON_IMPORT });
        clearAll(dispatch, !user.hasStateMemory, false, solution.show);
        handleSetSolutionParameter({ param: { name: files[0].name } });

        handleJsonDrop(
          dispatch,
          files,
          mode,
          error,
          appMode,
          cookies,
          oAuth,
          user,
          element,
          solution.show,
          false,
          depotMarkerTrans,
          returnLocationMarkerTrans,
        );
      }
    },
    [
      error,
      cookies,
      dispatch,
      oAuth,
      user,
      handleSetError,
      handleSetSolutionParameter,
      handleSetTourParameter,
      solution.show,
      depotMarkerTrans,
      returnLocationMarkerTrans,
    ],
  );

  const onChange = useCallback(
    (path, oldValue, newValue) => {
      if (newValue === '' || Number.isNaN(newValue)) {
        return;
      }
      handleSetSolutionJson({ jsonNoTerritories: false });
      oldValueRef.current.push({ path, oldValue, adjustedPath: formatErrorPath(path) });
      setJsonObject((prev) =>
        removeProperties(applyValue(prev, path, removeAllWhitespaces(newValue))),
      );
      handleSetProblemJsonParam({
        path,
        value: removeAllWhitespaces(newValue),
        index: solution.show,
      });

      handleSetSolutionJson({ json: true, jsonUserChange: true, jsonPending: true });
      handleSetError();
    },
    [handleSetSolutionJson, handleSetProblemJsonParam, handleSetError, solution.show, error],
  );

  const onCopy = useCallback(
    (path, obj, copy) => {
      const toCopy =
        path.length === 0 || path.length === undefined
          ? JSON.stringify(jsonObject)
          : `"${path.slice(-1).pop()}": ${JSON.stringify(get(jsonObject, path))}`;

      copy(toCopy);
    },
    [jsonObject],
  );

  const onJsonCopy = useCallback(() => {
    navigator.clipboard.writeText(JSON.stringify(jsonObject));
    toast(successfulCopy, {
      autoClose: 1000,
      hideProgressBar: true,
      closeButton: false,
    });
  }, [jsonObject, successfulCopy]);

  const onPaste = useCallback(async () => {
    let json = {};
    try {
      const text = await navigator.clipboard.readText();
      json = convertToValidJson(removeAllWhitespaces(text));
    } catch {
      handleSetError(error.incorrectJsonObject);
      return;
    }
    handleSetSolutionJson({ jsonPasting: true });
    const isSol = !!json?.tours || !!json?.statistic;
    const noTours = isSol && json?.tours?.length === 0;

    if (noTours) handleSetSolutionJson({ jsonNoTours: true });

    const jsonCheck = checkForValidTPJson(json, isSol, pasteErrors);
    if (jsonCheck) {
      handleSetError(jsonCheck);
      return;
    }
    toast(successfulPaste, {
      autoClose: 1000,
      hideProgressBar: true,
      closeButton: false,
    });

    dispatch(clearSolution());

    const cleanedJson = removeProperties(json);
    setJsonObject(cleanedJson);
    setJustPastedJson(true);

    handleSetSolutionParameter({ param: { reasons: null, name: 'pasted' }, index: solution.show });
    integrateFileText(
      JSON.stringify(cleanedJson),
      'upload',
      'pasted',
      user,
      cookies,
      oAuth,
      dispatch,
      true,
      error,
      solution.show,
      false,
    );

    handleSetTourParameter({ ordersMode: METHODS.JSON_IMPORT, index: solution.show });
    handleSetDisplayParameter({ noMapMovement: false });
  }, [
    dispatch,
    user,
    cookies,
    oAuth,
    error,
    solution.show,
    handleSetError,
    handleSetSolutionJson,
    handleSetSolutionParameter,
    handleSetTourParameter,
    handleSetDisplayParameter,
    pasteErrors,
    successfulPaste,
  ]);

  const bigFile =
    (jsonObject?.tours && jsonObject.tours.length > 30) ||
    (jsonObject?.plan?.jobs && jsonObject.plan.jobs.length > 100);

  const maxDisplayLength = bigFile ? 5 : 30;
  const defaultInspectDepth = bigFile ? 1 : 5;

  const updateAssignAt = (groups, idToUpdate, newValue) =>
    groups.map((group) => ({
      ...group,
      pudos: group.pudos.map((pudo) =>
        pudo.id === idToUpdate ? set({ ...pudo }, 'assignAt', newValue) : pudo,
      ),
    }));

  useEffect(() => {
    if (solution.jsonUserChange || resetValue || (pathReg && oldValueRef.current.length !== 0)) {
      setResetValue(false);
      handleSetSolutionParameter({ param: { reasons: null }, index: solution.show });

      if (!fileName && !solution.jsonUnassigned)
        handleSetSolutionParameter({ param: { name: 'problem' } });
      if (solution.jsonPaste) {
        handleSetSolutionParameter({ param: { name: 'pasted' } });
        integrateFileText(
          JSON.stringify(jsonObject),
          'upload',
          'pasted',
          user,
          cookies,
          oAuth,
          dispatch,
          true,
          error,
          solution.show,
          false,
        );
      } else if (solution.jsonUserChange === 'draft') {
        recalculateMapData(
          dispatch,
          jsonObject,
          getLastValidIteration(solution.requests[solution.show])?.stopsNoLocation,
          territories.areaDetails,
          solution.show,
          solution.jsonUserChange === 'orders' && orders,
          depotMarkerTrans,
          returnLocationMarkerTrans,
        );
      } else {
        if (solution.jsonUserChange === 'orders') {
          const draggedOrder = orders.find((order) => order.Dragged);
          const jsonJobs = jsonObject.plan.jobs;
          const jsonOrder = jsonJobs.find((order) => order.id === draggedOrder.InternalID);
          if (jsonOrder) {
            const changedOrder = changeJsonOrderLocation(jsonOrder, draggedOrder);
            const jsonIdx = jsonJobs.indexOf(jsonOrder);
            const orderIdx = orders.indexOf(draggedOrder);
            jsonJobs[jsonIdx] = changedOrder;
            orders[orderIdx].Dragged = null;
          } else {
            const newJob = convertOrderToJson(draggedOrder);
            jsonObject.plan.jobs = [newJob, ...jsonJobs];
          }
        }
        if (solution.jsonUserChange === 'ordersDepot') {
          const location = tourPlanner.location.value;
          jsonObject.fleet.types[0].shifts[0].start.location = location;
        }
        if (solution.jsonUserChange === 'ordersEndLoc') {
          const location = tourPlanner.returnLocation.value;
          jsonObject.fleet.types[0].shifts[0].end.location = location;
        }
        if (solution.jsonUserChange === 'pudos') {
          const groups = tourPlanner.groups;
          jsonObject.plan.groups = groups;
        }
        if (solution.jsonUserChange === 'pudo') {
          const pudoUpdate = tourPlanner.pudoUpdate;
          const groups = updateAssignAt(jsonObject.plan.groups, pudoUpdate.name, pudoUpdate.value);
          jsonObject.plan.groups = groups;
        }
        const hasManualOrders =
          tourPlanner?.editedOrder && Object.keys(tourPlanner.editedOrder).length > 0;
        const ordersUserChange =
          solution.jsonUserChange === 'orders' ||
          solution.jsonUserChange === 'ordersDepot' ||
          solution.jsonUserChange === 'ordersEndLoc';

        if (!isEmpty(jsonObject)) {
          recalculateSolutionParams(
            jsonObject,
            dispatch,
            error,
            fileName,
            getLastValidIteration(solution.requests[solution.show])?.stopsNoLocation,
            solution.show,
            false,
            fileExt,
            user.distance === 'imperial',
            (hasManualOrders || ordersUserChange || solution.jsonUserChange === 'manual') && orders,
            depotMarkerTrans,
            returnLocationMarkerTrans,
            ordersUserChange || solution.jsonUserChange === 'manual',
            hasManualOrders,
            solution.jsonUserChange === 'ordersDepot' || solution.jsonUserChange === 'ordersEndLoc',
            user,
            solution.jsonNoTerritories,
            tourPlanner,
          );
        }

        if (user.requestType === async) {
          handleSetSolutionParameter({
            param: { status: null, statusHref: null },
            index: solution.show,
          });
        }
      }
    }
  }, [
    cookies,
    dispatch,
    error,
    fileName,
    handleSetSolutionParameter,
    jsonObject,
    oAuth,
    pathReg,
    resetValue,
    solution.jsonPaste,
    solution.jsonUserChange,
    solution.requests,
    solution.show,
    solution.jsonUnassigned,
    territories,
    tourPlanner.ordersMode,
    user,
    fileExt,
    orders,
    depotMarkerTrans,
    returnLocationMarkerTrans,
    tourPlanner.editedOrder,
    tourPlanner.location.value,
    tourPlanner.returnLocation.value,
    tourPlanner.groups,
    convertToTerritory,
  ]);

  useEffect(() => {
    if (errorState && pathReg && oldValueRef.current.length !== 0) {
      const find = oldValueRef.current.filter((v) => pathReg.includes(v.adjustedPath));
      if (find.length >= 1) {
        find.forEach((f) => {
          setResetValue(true);
          setJsonObject((prev) =>
            removeProperties(applyValue(prev, f.path, removeAllWhitespaces(f.oldValue))),
          );
          handleSetProblemJsonParam({
            path: f.path,
            value: removeAllWhitespaces(f.oldValue),
            index: solution.show,
          });
        });
      }

      oldValueRef.current = [];
    }
  }, [errorState, handleSetProblemJsonParam, pathReg, solution.show, setResetValue]);

  useEffect(() => {
    const currJson = user?.currentJson;
    if (!isEqual(currJson, jsonObject) && jsonObject?.fleet) {
      handleSetUserParam({ currentJson: jsonObject });
    }
  }, [jsonObject]);

  useEffect(() => {
    jsonObjectRef.current = jsonObject;
  }, [jsonObject]);

  useEffect(() => {
    if (
      convertToTerritory === 'enabled' &&
      updateRef.current === false &&
      tourData?.tours.length > 0
    ) {
      const assignOrders = assignTerritoriesToOrders(
        tourData?.tours,
        isEmpty(orders) ? tempOrders : orders,
      );
      if (!isEmpty(assignOrders)) {
        handleSetOrders(assignOrders);
        updateRef.current = true;

        handleSetSolutionJson({
          json: true,
          jsonUserChange: 'manual',
          jsonPending: true,
          jsonTriggerVRP: true,
          jsonNoTerritories: true,
        });

        handleSetUserParam({
          mapSettings: { ...user.mapSettings, convertToTerritory: false },
        });
        clearedTerritoriesRef.current = false;
      }
    }

    if (convertToTerritory === null && !clearedTerritoriesRef.current && isSolution) {
      dispatch(
        setTerritoriesFromProblemFile({
          groupAreas: [],
          territories: [],
          avoidAreas: [],
          excludeAreas: [],
        }),
      );
      const ords = isEmpty(orders) ? tempOrders : orders;
      const clearedOrders = (ords || []).map((order) => {
        const { Territories, ...rest } = order;
        return rest;
      });
      dispatch(setOrders(clearedOrders));
      handleSetSolutionJson({
        json: true,
        jsonUserChange: 'manual',
        jsonPending: true,
        jsonNoTerritories: true,
      });
      clearedTerritoriesRef.current = true;
    }
  }, [
    convertToTerritory,
    tourData,
    orders,
    tempOrders,
    handleSetOrders,
    handleSetSolutionJson,
    handleSetUserParam,
    user.mapSettings,
    dispatch,
    solution.show,
    isSolution,
  ]);

  useEffect(() => {
    const ords = isEmpty(orders) ? tempOrders : orders;
    const fleetEnabled = user.fleetEnabled;

    if (!isEmpty(ords) && fleetEnabled === undefined) {
      const lastIteration = last(solution.requests[solution.show].iterations);
      const avoidAreas = generateAvoidAreasFromFileProblem(lastIteration?.request.json, ords) || [];
      const excludeAreas =
        generateExcludeAreasFromFileProblem(lastIteration?.request.json, ords) || [];
      const { groupAreas = [], territories: territ = [] } = generatePolygonsFromOrders(ords);
      dispatch(
        setTerritoriesFromProblemFile({
          groupAreas,
          territories: territ,
          avoidAreas,
          excludeAreas,
          fromUploadedFile: false,
          noUserChange: true,
        }),
      );
    }

    if (convertToTerritory === null) {
      dispatch(
        setTerritoriesFromProblemFile({
          groupAreas: [],
          territories: [],
          avoidAreas: [],
          excludeAreas: [],
        }),
      );
      handleSetSolutionJson({
        jsonNoTerritories: true,
      });
    }
  }, [orders, dispatch, convertToTerritory, tempOrders, user?.fleetEnabled]);

  useEffect(() => {
    if (convertToTerritory === null) {
      const ords = isEmpty(orders) ? tempOrders : orders;
      const hasTerritories = (ords || []).some((order) => order.Territories);
      if (hasTerritories) {
        const clearedOrders = [];
        const seenIds = new Set();
        (ords || []).forEach(({ Territories, ...rest }) => {
          if (!seenIds.has(rest.InternalID)) {
            clearedOrders.push(rest);
            seenIds.add(rest.InternalID);
          }
        });
        dispatch(setOrders(clearedOrders));
        handleSetSolutionJson({ jsonTriggerVRP: true });
      }
    }
  }, [orders, convertToTerritory, dispatch, tempOrders]);

  useEffect(() => {
    if (
      !user.mapSettings?.hidePositions &&
      user.mapSettings?.positionsChange &&
      !positionsClearedRef.current
    ) {
      const ords = isEmpty(orders) ? tempOrders : orders;
      if (!isEmpty(ords) && ords.some((order) => order.Position)) {
        const seenIds = new Set();
        const clearedOrders = [];
        ords.forEach(({ Position, ...rest }) => {
          if (!seenIds.has(rest.InternalID)) {
            clearedOrders.push(rest);
            seenIds.add(rest.InternalID);
          }
        });
        dispatch(setOrders(clearedOrders, false, solution.show));
        handleSetSolutionJson({ jsonTriggerVRP: true });
        positionsClearedRef.current = true;
      }
    }
    if (!user.mapSettings?.positionsChange || user.mapSettings?.hidePositions) {
      positionsClearedRef.current = false;
    }

    if (
      !user.mapSettings?.hideGroups &&
      user.mapSettings?.groupsChange &&
      !groupsClearedRef.current
    ) {
      const ords = isEmpty(orders) ? tempOrders : orders;
      if (!isEmpty(ords) && ords.some((order) => order.Group)) {
        const seenIds = new Set();
        const clearedOrders = [];
        ords.forEach(({ Group, ...rest }) => {
          if (!seenIds.has(rest.InternalID)) {
            clearedOrders.push(rest);
            seenIds.add(rest.InternalID);
          }
        });
        dispatch(setOrders(clearedOrders, false, solution.show));
        handleSetSolutionJson({ jsonTriggerVRP: true });
        groupsClearedRef.current = true;
      }
    }
    if (!user.mapSettings?.groupsChange || user.mapSettings?.hideGroups) {
      groupsClearedRef.current = false;
    }

    if (
      !user.mapSettings?.hideMaxTimeOnVehicle &&
      user.mapSettings?.maxTimeChange &&
      !maxTimeClearedRef.current
    ) {
      const ords = isEmpty(orders) ? tempOrders : orders;
      if (!isEmpty(ords) && ords.some((order) => order.MaxTime)) {
        const seenIds = new Set();
        const clearedOrders = [];
        ords.forEach(({ MaxTime, ...rest }) => {
          if (!seenIds.has(rest.InternalID)) {
            clearedOrders.push(rest);
            seenIds.add(rest.InternalID);
          }
        });
        dispatch(setOrders(clearedOrders, false, solution.show));
        handleSetSolutionJson({ jsonTriggerVRP: true });
        maxTimeClearedRef.current = true;
      }
    }
    if (!user.mapSettings?.maxTimeChange || user.mapSettings?.hideMaxTimeOnVehicle) {
      maxTimeClearedRef.current = false;
    }

    if (
      !user.mapSettings?.hidePriority &&
      user.mapSettings?.priorityChange &&
      !priorityClearedRef.current
    ) {
      const ords = isEmpty(orders) ? tempOrders : orders;
      if (!isEmpty(ords) && ords.some((order) => order.Priority)) {
        const seenIds = new Set();
        const clearedOrders = [];
        ords.forEach(({ Priority, ...rest }) => {
          if (!seenIds.has(rest.InternalID)) {
            clearedOrders.push(rest);
            seenIds.add(rest.InternalID);
          }
        });
        dispatch(setOrders(clearedOrders, false, solution.show));
        handleSetSolutionJson({ jsonTriggerVRP: true });
        priorityClearedRef.current = true;
      }
    }
    if (!user.mapSettings?.priorityChange || user.mapSettings?.hidePriority) {
      priorityClearedRef.current = false;
    }
  }, [
    user.mapSettings?.hideGroups,
    user.mapSettings?.groupsChange,
    user.mapSettings?.hideMaxTimeOnVehicle,
    user.mapSettings?.maxTimeChange,
    user.mapSettings?.hidePositions,
    user.mapSettings?.positionsChange,
    user.mapSettings?.hidePriority,
    user.mapSettings?.priorityChange,
    solution.show,
    dispatch,
    orders,
    tempOrders,
    handleSetSolutionJson,
  ]);

  useEffect(() => {
    updateRef.current = false;
  }, [convertToTerritory]);

  useEffect(() => {
    const ords = isEmpty(orders) ? tempOrders : orders;
    const fleetEnabled = user.fleetEnabled;

    if (isEmpty(ords) || fleetEnabled === false) {
      dispatch(
        setTerritoriesFromProblemFile({
          groupAreas: [],
          territories: [],
          avoidAreas: [],
          excludeAreas: [],
        }),
      );
    }
  }, [orders, tempOrders, dispatch, user?.fleetEnabled]);

  useEffect(() => {
    if (isEmpty(jsonObject)) {
      handleSetSolutionJson({
        jsonWizard: true,
      });
    }
  }, [jsonObject]);

  const JsonComponent = (
    <JsonViewer
      id="react-file-drop-demo"
      value={jsonObject}
      theme={OCEAN_THEME}
      displayDataTypes={false}
      highlightUpdates
      rootName={false}
      editable={isEditable}
      onChange={onChange}
      onCopy={onCopy}
      displaySize={selectedOption === OPTIONS.PROBLEM || !bigFile}
      quotesOnKeys={false}
      maxDisplayLength={maxDisplayLength}
      defaultInspectDepth={defaultInspectDepth}
      collapseStringsAfterLength={30}
      indentWidth={4}
    />
  );

  useEffect(() => {
    if (solution.json) setSelectedOption(OPTIONS.PROBLEM);
  }, [solution.json]);

  useEffect(() => {
    document.addEventListener('keydown', escFunction, false);
    if (user.jsonPanelOpen) setShowJsonPanel(user.jsonPanelOpen);

    return () => {
      document.removeEventListener('keydown', escFunction, false);
      if (!showJsonPanel) {
        handleSetUserParam({ jsonTourPanelOpen: false });
      }
    };
  }, [user.jsonPanelOpen, user.jsonTourPanelOpen, escFunction, showJsonPanel, handleSetUserParam]);

  return (
    <>
      <Modal isVisible={showJsonPanel} isFull fromRight>
        <StyledContrastPanel fullDisplay>
          <DevConsoleHeader
            setJsonObject={setJsonObject}
            setSelectedOption={setSelectedOption}
            setIsTourPanelOpen={setIsTourPanelOpen}
            isTourPanelOpen={isTourPanelOpen}
            selectedOption={selectedOption}
            togglePanel={togglePanel}
            escFunction={escFunction}
            showPanel={showJsonPanel}
            errorState={errorState}
            justPastedJson={justPastedJson}
            setJustPastedJson={setJustPastedJson}
          />
          <StyledJsonContainer ref={jsonRef} className="json-viewer-panel">
            <StyledFloatingBtnContainer>
              <ToastContainer hideProgressBar closeButton={false} />
              <StyledFloatingBtn
                data-test-id="pasteJson"
                icon={pasteIcon}
                onClick={onPaste}
                title={pasteJson}
                disabled={pasteDisabled}
              />
              <StyledFloatingBtn icon={copyIcon} onClick={onJsonCopy} order={2} title={copyJson} />
            </StyledFloatingBtnContainer>
            <FileUploadArea
              component={JsonComponent}
              jsonUpload
              handleJsonDrop={handleDrop}
              isJsonUploadClicked={isJsonUploadClicked}
              setIsJsonUploadClicked={setIsJsonUploadClicked}
              jsonRef={jsonRef.current}
              className="json-drop"
            />
          </StyledJsonContainer>
          <DevConsoleFooter
            setIsJsonUploadClicked={setIsJsonUploadClicked}
            selectedOption={selectedOption}
            jsonObject={jsonObject}
            filename={fileName}
            oAuth={oAuth}
            handleSetError={handleSetError}
            tourPlanner={tourPlanner}
          />
        </StyledContrastPanel>
      </Modal>
      {!showJsonPanel && (
        <StyledAccessContainer
          data-test-id="JSONViewer"
          onClick={() => togglePanel()}
          title={devConsoleLabels.configTitle}
        >
          <div>&nbsp;</div>
        </StyledAccessContainer>
      )}
    </>
  );
};

export default withTranslation(DevConsole);
